import React, { useEffect, useState } from 'react'
import {
  Alert, Box, Button, CircularProgress, Divider, Drawer, IconButton, Stack,
  Step, StepContent, StepLabel, Stepper, Tooltip, Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined'
import { useSnackbar } from 'notistack'
import StatusChip from './StatusChip'
import { fetchDetail, fetchDownloadUrl, fetchInputFileUrl, retryGeneration, extractPiiError } from '../api'
import { formatDate1 } from '../../../common/util'

const ACTION_LABELS = {
  SUBMITTED: 'Request submitted',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  PROCESSING: 'File generation started',
  COMPLETED: 'Output file generated',
  FAILED: 'File generation failed',
  EXPIRED: 'File expired & deleted',
  DOWNLOADED: 'Output downloaded',
  INPUT_PURGED: 'Input file purged',
}

function SummaryRow({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between" sx={{ py: 0.4 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'right', maxWidth: '60%' }}>
        {value == null || value === '' ? '—' : String(value)}
      </Typography>
    </Stack>
  )
}

/**
 * Request detail (BRD §7.5): header, file summary, approval timeline sourced from
 * statusHistory. Also hosts owner Download and (L2-only) Retry actions.
 */
export default function RequestDetailDrawer({ requestId, open, onClose, isL2, onChanged }) {
  const { enqueueSnackbar } = useSnackbar()
  const [detail, setDetail] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [acting, setActing] = useState(false)

  useEffect(() => {
    if (!open || !requestId) return
    setLoading(true)
    setError(null)
    fetchDetail(requestId)
      .then((res) => setDetail(res.data))
      .catch((err) => setError(extractPiiError(err, 'Failed to load request detail')))
      .finally(() => setLoading(false))
  }, [open, requestId])

  const openSignedUrl = (fetcher, errorFallback) => {
    setActing(true)
    fetcher(requestId)
      .then((res) => {
        const url = res.data?.downloadUrl
        if (url) window.open(url, '_blank', 'noopener')
        else enqueueSnackbar('No download URL returned', { variant: 'error' })
      })
      .catch((err) => enqueueSnackbar(extractPiiError(err, errorFallback), { variant: 'error' }))
      .finally(() => setActing(false))
  }

  const onRetry = () => {
    setActing(true)
    retryGeneration(requestId)
      .then(() => {
        enqueueSnackbar('File generation re-triggered', { variant: 'success' })
        onChanged && onChanged()
        onClose()
      })
      .catch((err) => enqueueSnackbar(extractPiiError(err, 'Retry failed'), { variant: 'error' }))
      .finally(() => setActing(false))
  }

  const history = detail?.statusHistory || []

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 480 } } }}>
      <Box sx={{ p: 2.5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">{requestId}</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Stack>

        {loading && <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>}
        {error && <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>}

        {detail && !loading && (
          <>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, mb: 2 }}>
              <StatusChip status={detail.status} />
              {detail.status === 'FAILED' && (
                <Alert severity="error" sx={{ py: 0, flex: 1 }}>
                  File generation failed — the tech team has been notified.
                </Alert>
              )}
            </Stack>

            <Typography variant="subtitle2" gutterBottom>Request</Typography>
            <SummaryRow label="Requested by" value={`${detail.requestedByName || ''} (${detail.requestedByEmail || ''})`} />
            <SummaryRow label="Submitted on" value={detail.submittedOn ? formatDate1(detail.submittedOn) : null} />
            <SummaryRow label="Reason" value={detail.reason} />

            <Divider sx={{ my: 1.5 }} />
            <Typography variant="subtitle2" gutterBottom>Input file</Typography>
            <SummaryRow label="File" value={detail.inputFileName} />
            <SummaryRow label="Total rows" value={detail.totalRows} />
            <SummaryRow label="Valid AWBs" value={detail.awbCount} />
            <SummaryRow label="Invalid rows" value={detail.invalidRows} />
            <SummaryRow label="Duplicates dropped" value={detail.duplicateRows} />
            <Button
              size="small"
              sx={{ mt: 0.5 }}
              disabled={acting}
              onClick={() => openSignedUrl(fetchInputFileUrl, 'Input file unavailable')}
            >
              View AWB list
            </Button>

            {(detail.status === 'COMPLETED' || detail.status === 'EXPIRED') && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="subtitle2" gutterBottom>Output file</Typography>
                <SummaryRow label="Matched AWBs" value={detail.matchedAwbCount} />
                <SummaryRow label="Not found" value={detail.notFoundAwbCount} />
                <SummaryRow label="Downloads" value={detail.downloadCount} />
                <SummaryRow label="Expires on" value={detail.expiresOn ? formatDate1(detail.expiresOn) : null} />
              </>
            )}

            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              {detail.status === 'COMPLETED' && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<DownloadOutlinedIcon />}
                  disabled={acting}
                  onClick={() => openSignedUrl(fetchDownloadUrl, 'Download not available')}
                >
                  Download output CSV
                </Button>
              )}
              {detail.status === 'EXPIRED' && (
                <Tooltip title={`File expired${detail.expiresOn ? ` on ${formatDate1(detail.expiresOn)}` : ''}. Raise a new request.`}>
                  <span><Button size="small" variant="contained" disabled startIcon={<DownloadOutlinedIcon />}>Download</Button></span>
                </Tooltip>
              )}
              {isL2 && (detail.status === 'FAILED' || detail.status === 'PROCESSING') && (
                <Button size="small" variant="outlined" color="warning" startIcon={<ReplayOutlinedIcon />} disabled={acting} onClick={onRetry}>
                  Retry generation
                </Button>
              )}
            </Stack>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>Timeline</Typography>
            <Stepper orientation="vertical" activeStep={-1} sx={{ '& .MuiStepConnector-line': { minHeight: 12 } }}>
              {history.map((step, i) => (
                <Step key={i} expanded completed>
                  <StepLabel>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {ACTION_LABELS[step.action] || step.action}
                      {step.level ? ` — ${step.level}` : ''}
                    </Typography>
                  </StepLabel>
                  <StepContent>
                    <Typography variant="caption" color="text.secondary">
                      {step.actionBy}{step.date ? ` · ${formatDate1(step.date)}` : ''}
                    </Typography>
                    {step.remark && (
                      <Typography variant="body2" sx={{ mt: 0.3 }}>“{step.remark}”</Typography>
                    )}
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          </>
        )}
      </Box>
    </Drawer>
  )
}
